import { useEffect, useState, useCallback } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV } from "@/lib/data";
import { getLenis } from "@/components/SmoothScroll";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  const toTop = useCallback((smooth = true) => {
    const lenis = getLenis();
    if (lenis) lenis.scrollTo(0, smooth ? { duration: 0.8 } : { immediate: true });
    else window.scrollTo({ top: 0, behavior: smooth ? "smooth" : "auto" });
  }, []);

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,padding,box-shadow] duration-500 ${
          scrolled
            ? "border-b border-line bg-white/85 py-3 shadow-[0_10px_30px_-20px_rgba(23,21,15,0.4)] backdrop-blur-xl"
            : "border-b border-transparent py-5"
        }`}
      >
        <nav className="mx-auto flex max-w-[1440px] items-center justify-between px-6 md:px-12">
          <Link to="/" data-testid="logo-link" onClick={() => { if (pathname === "/") toTop(true); }} className="relative z-10 flex items-center" aria-label="Get Visible — início">
            <img src="/brand/logo-dark.png" alt="Logótipo Get Visible — Website Design" className="h-11 w-auto md:h-12 transition-transform duration-300 hover:scale-105" />
          </Link>

          <ul className="hidden items-center gap-8 lg:flex">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`nav-${item.to === "/" ? "home" : item.to.slice(1)}`}
                  onClick={() => toTop()}
                  className={({ isActive }) =>
                    `link-underline text-sm tracking-wide transition-colors duration-300 ${
                      isActive ? "text-ink" : "text-muted-foreground hover:text-ink"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="hidden lg:block">
            <Link
              to="/contacto"
              data-testid="nav-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-grad px-6 py-3 text-sm font-medium text-white shadow-[0_10px_30px_-12px_rgba(79,111,214,0.7)] transition-transform duration-300 hover:-translate-y-0.5"
            >
              Pedir orçamento
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <button
            data-testid="menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="relative z-10 flex h-11 w-11 items-center justify-center rounded-full border border-line text-ink lg:hidden"
            aria-label="Abrir menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 flex flex-col bg-ink px-6 pb-10 pt-28 lg:hidden"
          >
            <ul className="flex flex-col gap-1">
              {NAV.map((item, i) => (
                <motion.li
                  key={item.to}
                  initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink to={item.to} end={item.to === "/"} onClick={() => { setOpen(false); toTop(false); }} className="block border-b border-white/10 py-4 font-display text-3xl font-medium text-white">
                    {item.label}
                  </NavLink>
                </motion.li>
              ))}
            </ul>
            <Link to="/contacto" className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-grad px-6 py-4 text-base font-medium text-white">
              Pedir orçamento <ArrowUpRight className="h-5 w-5" />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
